<script>
	import page from 'page';
	import model from 'model/model';
	import googleClassroomUtilities from 'utilities/googleClassroomUtilities';
	import accountUtilities from 'utilities/accountUtilities';
	import { loadClasses } from 'utilities/classUtilities';
	import uC from 'core/userContext';
	import GoogleLogo from 'components/svg/google-logo.vue';
	import BookbagIcon from 'components/svg/bookbag-icon.vue';
	import TeacherIcon from 'components/svg/teacher-icon.vue';

	const { setGCAddon, getGenericGCAuthURL } = googleClassroomUtilities;

	export default {
		components: {
			'google-logo': GoogleLogo,
			'bookbag-icon': BookbagIcon,
			'teacher-icon': TeacherIcon
		},
		modelData: ['localGCAccessToken', 'gcAddon', 'userContext', 'classes', 'gcAuthURL'],
		data() {
			return {
				reauth: false
			};
		},
		computed: {
			isTeacherView() {
				const { initialPath = '' } = this.gcAddon || {};

				const pathSegment = initialPath.split('/')[1];
				const pathDict = {
					'classroom-addon': 'teacher', // attachment discovery
					assignment: 'teacher', // teacher view & student work review
					'student-workspace': 'student', // student view
					'article-dashboard': 'student' // student-view (AAD)
				};

				return pathDict[pathSegment] === 'teacher';
			},
			redirectUrl() {
				const classes = this.classes || [];
				const { initialPath, courseId } = this.gcAddon || {};

				if (initialPath !== '/classroom-addon') {
					// return to uri set at attachment creation
					return initialPath;
				}

				if (uC.isTeacher()) {
					const hasClass = classes.some(c => c.gcid === courseId);

					if (!classes || classes.length === 0) {
						return '/onboarding-role';
					} else if (!hasClass) {
						return '/class-admin';
					}
				}

				return '/find-content';
			},
			googleUrl() {
				const searchParams = new URLSearchParams({
					addon: true
				});

				const { login_hint, hd, initialPath } = this.gcAddon || {};

				if (login_hint) {
					searchParams.set('login_hint', login_hint);
				} else {
					searchParams.set('hd', hd);
				}

				if (this.isTeacherView) {
					searchParams.set('role', 'teacher');
				}

				if ((window.readworks.config.rwtesting && !window.readworks.config.rwUnitTesting) || (initialPath && (this.reauth || !this.isTeacherView))) {
					searchParams.set('url', initialPath);
				}

				return getGenericGCAuthURL(searchParams);
			},
			isValidSession() {
				const userContext = this.userContext;
				const localGCAccessToken = this.localGCAccessToken;
				const { login_hint = '' } = this.gcAddon || {};
				let sub;

				if (userContext && localGCAccessToken) {
					if (uC.isStudent()) {
						sub = userContext.username;
					} else {
						sub = userContext.google;
					}
				}
				return sub === login_hint;
			}
		},
		methods: {
			async authenticate() {
				const gcAuthURL = this.googleUrl;

				if (window.readworks.config.rwtesting && !window.readworks.config.rwUnitTesting) {
					page(gcAuthURL);
					return;
				}
				// set via model to avoid race condition
				// nextTick necessary if modelData props used
				model.set('gcAuthURL', gcAuthURL);
				model.set('gcAddon', Object.assign({}, this.gcAddon, { gcAuthURL }));

				const message = await googleClassroomUtilities.authenticateWithGoogleClassroom();

				const { googleToken, rwToken, urlParams } = message;

				if (rwToken) {
					await accountUtilities.getFromToken(rwToken);
					model.set('localGCAccessToken', googleToken);
				} else {
					page.redirect(`/student-authentication${urlParams}`);
				}
			}
		},
		created() {
			if (!this.gcAddon) {
				setGCAddon();
			}

			const reauth = location.search.includes('reauth');

			if (reauth && !this.isTeacherView) {
				// SameSite cookies cannot be read in an iframe
				// Teachers must manually reauth
				this.reauth = reauth;
				page.redirect(this.googleUrl);
			}
		},
		watch: {
			async userContext(context) {
				if (this.isValidSession) {
					if (uC.isTeacher()) {
						await loadClasses();
					}
					page.redirect(this.redirectUrl);
				} else if (context && (context.username || context.google)) {
					page.redirect('/logout');
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/media';

	.classroom-addon {
		.wrapper {
			display: flex;
		}

		.icon-container {
			display: none;
			background-color: $c-grape-main;
			height: 100vh;

			&.student {
				background-color: $c-green-main;
			}

			svg {
				height: 100%;
			}

			@include media(med) {
				display: flex;
				align-items: center;
			}
		}

		.addon-wrapper {
			flex: 1;
			padding: 1rem;
			text-align: center;

			h1 {
				margin: 2rem 0 5rem;
			}
		}
	}
</style>
<template>
	<main
		v-if="!isValidSession"
		class="classroom-addon main">
		<div class="wrapper">
			<aside
				class="icon-container"
				:class="{ student: !isTeacherView }">
				<teacher-icon v-if="isTeacherView"></teacher-icon>
				<bookbag-icon v-else></bookbag-icon>
			</aside>
			<section class="addon-wrapper">
				<h1>Authenticate With Google</h1>
				<button
					class="button -google"
					@click="authenticate">
					<google-logo></google-logo>
					<span>Sign in with Google</span>
				</button>
			</section>
		</div>
	</main>
</template>
